import React, { useState, useEffect, useCallback } from "react";
import { useErrorNotification } from "../../context/ErrorNotificationContext";
import {
  fetchAdminUsers,
  fetchAllSpacesGrouped,
  fetchUserSpaceAccess,
  assignUserToSpaces,
  AdminUser,
  SpaceDto,
  GroupedSpacesDto,
  UserSpaceAccessDto,
} from "../../api/api";
import {
  Search20Regular,
  ChevronDown20Regular,
  ChevronUp20Regular,
  ChevronLeft20Regular,
  ChevronRight20Regular,
  Checkmark20Regular,
  Building20Regular,
} from "@fluentui/react-icons";
import styles from "./UserSpaceAccessManager.module.css";

// ── Category metadata for display labels ──────────────────────────────────────
const CATEGORY_LABELS: Record<string, string> = {
  OfficialDocumentation: "Official Documentation",
  CustomerProject: "Customer Specific Documentation",
  ProductRoadmap: "Roadmap",
  InternalUnofficial: "Internal / Unofficial Documentation",
};

const getCategoryLabel = (category: string) =>
  CATEGORY_LABELS[category] ?? category;

// ── Helper: group spaces by category ─────────────────────────────────────────
// Note: Data is already grouped by the API, so this function is no longer needed.
// Keeping the grouping helper in case it's useful for future refactoring.
const groupSpacesByCategory = (groupedSpaces: GroupedSpacesDto[]) => {
  const groups: Record<string, SpaceDto[]> = {};
  for (const group of groupedSpaces) {
    groups[group.category] = group.spaces;
  }
  return groups;
};

// ── Component ────────────────────────────────────────────────────────────────
export const UserSpaceAccessManager: React.FC = () => {
  const { showError } = useErrorNotification();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [spaces, setSpaces] = useState<GroupedSpacesDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for selected user and their space assignments
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userSpaces, setUserSpaces] = useState<UserSpaceAccessDto[]>([]);
  const [selectedSpaceIds, setSelectedSpaceIds] = useState<Set<string>>(new Set());
  const [spacesLoading, setSpacesLoading] = useState(false);
  const [spacesModified, setSpacesModified] = useState(false);
  const [savingSpaces, setSavingSpaces] = useState(false);

  // Search and pagination
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 5;

  // State for category expansion
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );

  // Load all users and spaces on mount
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [usersData, spacesData] = await Promise.all([
        fetchAdminUsers(),
        fetchAllSpacesGrouped(),
      ]);
      setUsers(usersData);
      setSpaces(spacesData);

      // Auto-expand first category
      if (spacesData.length > 0) {
        setExpandedCategories(new Set([spacesData[0].category]));
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load data";
      setError(msg);
      showError(msg);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load user's current space assignments when selected
  const loadUserSpaces = useCallback(async (userId: string) => {
    try {
      setSpacesLoading(true);
      const data = await fetchUserSpaceAccess(userId);
      setUserSpaces(data);
      setSelectedSpaceIds(new Set(data.map((s) => s.spaceId)));
      setSpacesModified(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load user spaces";
      showError(msg);
    } finally {
      setSpacesLoading(false);
    }
  }, [showError]);

  const handleSelectUser = (userId: string) => {
    setSelectedUserId(userId);
    loadUserSpaces(userId);
  };

  const handleSpaceToggle = (spaceId: string) => {
    const newSelection = new Set(selectedSpaceIds);
    if (newSelection.has(spaceId)) {
      newSelection.delete(spaceId);
    } else {
      newSelection.add(spaceId);
    }
    setSelectedSpaceIds(newSelection);
    setSpacesModified(true);
  };

  const handleCategoryToggle = (category: string, categorySpaces?: SpaceDto[]) => {
    // If categorySpaces is provided, toggle selection; otherwise toggle expansion
    if (categorySpaces) {
      const categoryIds = new Set(categorySpaces.map((s) => s.spaceId));
      const allSelected = categorySpaces.every((s) =>
        selectedSpaceIds.has(s.spaceId)
      );

      const newSelection = new Set(selectedSpaceIds);
      if (allSelected) {
        categoryIds.forEach((id) => newSelection.delete(id));
      } else {
        categoryIds.forEach((id) => newSelection.add(id));
      }
      setSelectedSpaceIds(newSelection);
      setSpacesModified(true);
    } else {
      // Toggle category expansion
      setExpandedCategories((prev) => {
        const next = new Set(prev);
        if (next.has(category)) {
          next.delete(category);
        } else {
          next.add(category);
        }
        return next;
      });
    }
  };

  const handleSaveSpaces = async () => {
    if (!selectedUserId) return;

    // Minimum-space rule: a user must always have at least one assigned space.
    if (selectedSpaceIds.size === 0) {
      showError("At least one space must be assigned.");
      return;
    }

    try {
      setSavingSpaces(true);
      await assignUserToSpaces(selectedUserId, Array.from(selectedSpaceIds));
      setSpacesModified(false);
      // Reload the user's current assignments to confirm
      await loadUserSpaces(selectedUserId);
      showError("Spaces assigned successfully", "Success", "success");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to save spaces";
      showError(msg);
    } finally {
      setSavingSpaces(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedSpaceIds(new Set());
    setSpacesModified(true);
  };

  // Filter users by search
  const filteredUsers = users.filter((u) =>
    (u.email?.toLowerCase() ?? "").includes(search.toLowerCase()) ||
    (u.name?.toLowerCase() ?? "").includes(search.toLowerCase())
  );

  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const totalPages = Math.ceil(filteredUsers.length / PAGE_SIZE);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <span>Loading users and spaces...</span>
        </div>
      </div>
    );
  }

  if (error && !selectedUserId) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <span>⚠️ {error}</span>
        </div>
      </div>
    );
  }

  const spacesByCategory = spaces;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>
          <Building20Regular className={styles.titleIcon} />
          Space Access Management
        </h2>
        <p className={styles.subtitle}>
          Assign Confluence spaces to users for multi-space search access control.
        </p>
      </div>

      <div className={styles.mainContent}>
        {/* Left panel: User search and list */}
        <div className={styles.leftPanel}>
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Select User</h3>

            <div className={styles.searchBox}>
              <Search20Regular className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search by email or name..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className={styles.searchInput}
              />
            </div>

            <div className={styles.userList}>
              {paginatedUsers.length === 0 ? (
                <p className={styles.emptyMessage}>No users found.</p>
              ) : (
                paginatedUsers.map((user) => (
                  <button
                    key={user.userId}
                    className={`${styles.userItem} ${
                      selectedUserId === user.userId ? styles.userItemSelected : ""
                    }`}
                    onClick={() => handleSelectUser(user.userId)}
                  >
                    <div className={styles.userItemContent}>
                      <div className={styles.userItemName}>{user.name || user.email}</div>
                      <div className={styles.userItemEmail}>{user.email}</div>
                      {selectedUserId === user.userId && (
                        <Checkmark20Regular className={styles.userItemCheck} />
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>

            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  className={styles.paginationBtn}
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  <ChevronLeft20Regular />
                </button>
                <span className={styles.paginationInfo}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className={styles.paginationBtn}
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  <ChevronRight20Regular />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right panel: Space selection */}
        <div className={styles.rightPanel}>
          {selectedUserId ? (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Assign Spaces</h3>

              {spacesLoading ? (
                <div className={styles.loadingState}>
                  <div className={styles.spinner}></div>
                  <span>Loading spaces...</span>
                </div>
              ) : (
                <>
                  <div className={styles.spaceList}>
                    {spacesByCategory.length === 0 ? (
                      <p className={styles.emptyMessage}>No spaces available.</p>
                    ) : (
                      spacesByCategory.map((group) => {
                        const { category, spaces: categorySpaces } = group;
                        const categoryIds = categorySpaces.map((s) => s.spaceId);
                        const selectedCount = categoryIds.filter((id) =>
                          selectedSpaceIds.has(id)
                        ).length;
                        const allSelected = selectedCount === categoryIds.length;
                        const someSelected = selectedCount > 0 && !allSelected;
                        const isExpanded = expandedCategories.has(category);

                        return (
                          <div key={category} className={styles.categoryGroup}>
                            <button
                              type="button"
                              className={styles.categoryHeader}
                              onClick={() => handleCategoryToggle(category)}
                            >
                              <span className={styles.categoryLabel}>
                                {getCategoryLabel(category)}
                              </span>
                              <span className={styles.categoryCount}>
                                {allSelected
                                  ? "All selected"
                                  : someSelected
                                  ? `${selectedCount}/${categoryIds.length}`
                                  : `${categoryIds.length}`}
                              </span>
                              <input
                                type="checkbox"
                                className={styles.categoryCheckbox}
                                checked={allSelected}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleCategoryToggle(category, categorySpaces);
                                }}
                                aria-label={`Select all in ${getCategoryLabel(category)}`}
                              />
                            </button>

                            {isExpanded && (
                              <div className={styles.spaceItems}>
                                {categorySpaces.map((space) => {
                                  const checked = selectedSpaceIds.has(space.spaceId);
                                  return (
                                    <label
                                      key={space.spaceId}
                                      className={`${styles.spaceItem} ${
                                        checked ? styles.spaceItemChecked : ""
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        className={styles.spaceCheckbox}
                                        checked={checked}
                                        onChange={() => handleSpaceToggle(space.spaceId)}
                                      />
                                      <div className={styles.spaceItemContent}>
                                        <span className={styles.spaceItemName}>
                                          {space.spaceName}
                                        </span>
                                        <span className={styles.spaceItemId}>
                                          {space.spaceId}
                                        </span>
                                      </div>
                                      {checked && (
                                        <Checkmark20Regular className={styles.spaceItemCheck} />
                                      )}
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div className={styles.actions}>
                    <button
                      className={styles.clearBtn}
                      onClick={handleClearSelection}
                      disabled={selectedSpaceIds.size === 0 || savingSpaces}
                    >
                      Clear All
                    </button>
                    <button
                      className={`${styles.saveBtn} ${
                        !spacesModified || savingSpaces ? styles.saveBtnDisabled : ""
                      }`}
                      onClick={handleSaveSpaces}
                      disabled={!spacesModified || savingSpaces}
                    >
                      {savingSpaces ? "Saving..." : "Save Changes"}
                    </button>
                  </div>


                </>
              )}
            </div>
          ) : (
            <div className={styles.section}>
              <div className={styles.placeholderState}>
                <Building20Regular className={styles.placeholderIcon} />
                <p>Select a user to manage their space access</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
